import { useAtomValue } from "jotai";
import { useEffect, useMemo, useRef, useState } from "react";
import { Page, PageStatus } from "src/components";
import { useLoaders } from "src/components/InlineLoader";
import { Button, Color, Flex, Input, Pagination, SubgridTable, Text } from "src/elements";
import { usePaginationPage } from "src/elements/Pagination";

import {
  ResponseGetInputsMasterFixturesAll,
  RouteGetInputsMasterFixturesAll,
} from "@CommonApi/inputs/master-fixtures/all";
import { putInputsMasterFixturesReq, useApi } from "src/api";
import { isAxiosError } from "axios";
import type { MasterFixture } from "@CommonApi/inputs/master-fixtures/all";
import type { ErrorPutInputsMasterFixtures_masterFixtureId_ } from "@CommonApi/inputs/master-fixtures/_masterFixtureId_";
import { localeAtom } from "src/i18n";
import { translate } from "src/i18n/translate";
import { DEFAULT_LENGTH_UNIT } from "src/utils/lengthUnit";
import { loaderDepthErrorText } from "./store/LoaderDepthField";
import { useAsyncState } from "src/hooks/useAsyncState";
import { getFixturesTableColumns } from "./store/columns";
import { useToasts } from "src/components/Toasts";
import { createFixtureRowSaver, type FixtureEdit } from "./store/fixtureRowSaver";

export const PageFixtures = () => {
  const { getFixturesApi, putInputsMasterFixturesApi } = useApi();
  const { hasLoader } = useLoaders();
  const { toast } = useToasts();
  // The columns read the language through translate(); subscribing re-renders them on a change.
  useAtomValue(localeAtom);

  const [page, setPage] = usePaginationPage();
  const [storeInput, setStoreInput] = useState("");
  const [storeCode, setStoreCode] = useState("");
  const [response, setResponse] = useAsyncState<
    ResponseGetInputsMasterFixturesAll | null | undefined
  >(undefined);

  const isFixturesLoading = response === undefined || hasLoader(RouteGetInputsMasterFixturesAll);

  // The table as last saved, read by queued saves when they run (not as captured at render).
  const tableRef = useRef(response);
  const saveRow = useMemo(
    () =>
      createFixtureRowSaver(
        () => tableRef.current,
        (table) => {
          tableRef.current = table;
          setResponse(table);
        },
      ),
    [],
  );

  const handleUpdateFixture = async (row: MasterFixture, edit: FixtureEdit) => {
    try {
      // The api helper toasts and returns null on failure: keep the table on what is saved.
      await saveRow(
        row,
        edit,
        (payload) => putInputsMasterFixturesApi(String(row.master_fixture_id), payload),
        (saved) => !!saved,
      );
    } catch (error) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      toast({ title: "Failed to update fixture:", error });
    }
  };

  /** The spring loader depth goes on its own PUT, so a refusal shows under the cell. */
  const handleUpdateLoaderDepth = async (
    row: MasterFixture,
    loaderDepth: number | null,
  ): Promise<string | null> => {
    try {
      await saveRow(row, { loaderDepth }, (payload) =>
        putInputsMasterFixturesReq(String(row.master_fixture_id), payload),
      );
    } catch (error) {
      const body = (isAxiosError(error) ? error.response?.data : undefined) as
        | ErrorPutInputsMasterFixtures_masterFixtureId_
        | undefined;
      return body?.code
        ? loaderDepthErrorText({ code: body.code, params: body.params }, DEFAULT_LENGTH_UNIT)
        : translate("common", "shelfLoader.fixtures.notSaved");
    }
    return null;
  };

  const handleUpdateFrontOverhang = async (
    row: MasterFixture,
    maxFrontOverhang: number,
  ): Promise<string | null> => {
    try {
      const saved = await saveRow(
        row,
        { maxFrontOverhang },
        (payload) => putInputsMasterFixturesReq(String(row.master_fixture_id), payload),
        (result) => result.data.max_front_overhang === maxFrontOverhang,
      );
      if (saved.data.max_front_overhang !== maxFrontOverhang)
        return translate("common", "frontOverhang.fixtures.notConfirmed");
      return null;
    } catch {
      return translate("common", "frontOverhang.fixtures.notSaved");
    }
  };

  useEffect(() => {
    let active = true;
    tableRef.current = undefined;
    setResponse(undefined);
    getFixturesApi({ page, size: 20, ...(storeCode ? { storeCode } : {}) }).then((table) => {
      if (active) {
        tableRef.current = table;
        setResponse(table);
      }
    });
    return () => {
      active = false;
    };
  }, [page, storeCode]);

  useEffect(() => {
    return () => {
      setResponse(undefined);
    };
  }, []);

  return (
    <Page>
      <Flex column flexGrow gap="10px">
        <Flex padding="10px 0 0 0" justify="between" align="center">
          <Text variant="h3">Fixtures</Text>

          <Button width="120px" iconRight={{ name: "plus" }} isDisabled>
            Add Fixture
          </Button>
        </Flex>

        <Flex gap="10px" align="center">
          <Input
            title={translate("common", "frontOverhang.fixtures.storeCode")}
            value={storeInput}
            setValue={setStoreInput}
            width="220px"
            placeholder={translate("common", "frontOverhang.fixtures.allStores")}
          />
          <Button
            onClick={() => {
              setPage(1);
              setStoreCode(storeInput.trim());
            }}
          >
            {translate("common", "frontOverhang.fixtures.filter")}
          </Button>
          <Text>{translate("common", "frontOverhang.fixtures.note")}</Text>
        </Flex>
        <Flex column flexGrow gap="20px">
          {isFixturesLoading && <PageStatus label="Loading Fixtures..." icon="spinner" />}

          {!isFixturesLoading && !response && (
            <PageStatus label="Fixtures not found!" icon="alert" color={Color.red} />
          )}

          {!isFixturesLoading && response && (
            <Flex flexGrow>
              <SubgridTable
                columns={getFixturesTableColumns(
                  handleUpdateFixture,
                  handleUpdateLoaderDepth,
                  DEFAULT_LENGTH_UNIT,
                  handleUpdateFrontOverhang,
                )}
                data={response.data}
              />
            </Flex>
          )}
        </Flex>

        <Pagination
          page={page}
          setPage={setPage}
          totalPages={response?.totalPages || 1}
          isUsingInput
          isLoading={response === undefined}
        />
      </Flex>
    </Page>
  );
};
